import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Clock,
  MessageSquare,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import { format, startOfWeek, addWeeks, subWeeks, getISOWeek, parseISO, addDays } from 'date-fns'
import { useAuth } from '../../lib/auth'
import { StatusBadge } from '../../components/ui'
import { fetchWeeklySummary, saveWeeklyNotes } from '../../services/weeklySummary'
import type { WeeklySummaryData, WeeklySummaryAssignment } from '../../services/weeklySummary'
import toast from 'react-hot-toast'

const WEEKDAYS = ['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag', 'Söndag']

export function WeeklySummary() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const weekOptions = { weekStartsOn: 1 as const }

  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    startOfWeek(new Date(), weekOptions)
  )
  const [data, setData] = useState<WeeklySummaryData | null>(null)
  const [loading, setLoading] = useState(true)
  const [adminNotes, setAdminNotes] = useState('')
  const [adminNotesId, setAdminNotesId] = useState<string | null>(null)
  const [savingNotes, setSavingNotes] = useState(false)

  const weekStart = format(currentWeekStart, 'yyyy-MM-dd')
  const weekEnd = format(addDays(currentWeekStart, 6), 'yyyy-MM-dd')
  const weekNumber = getISOWeek(currentWeekStart)
  const isCurrentWeek = format(startOfWeek(new Date(), weekOptions), 'yyyy-MM-dd') === weekStart

  // Load data when week changes
  useEffect(() => {
    let cancelled = false
    setLoading(true)

    fetchWeeklySummary(weekStart)
      .then((result) => {
        if (cancelled) return
        setData(result)
        setAdminNotes(result.admin_notes || '')
        setAdminNotesId(result.admin_notes_id)
      })
      .catch((error) => {
        if (cancelled) return
        console.error('Error loading weekly summary:', error)
        toast.error('Kunde inte ladda veckosammanfattning')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [weekStart])

  // Save notes on blur
  const handleSaveNotes = useCallback(async () => {
    if (!user) return
    if (adminNotes === (data?.admin_notes || '')) return

    setSavingNotes(true)
    try {
      const result = await saveWeeklyNotes(weekStart, adminNotes, adminNotesId, user.id)
      setAdminNotesId(result.id || null)
    } catch {
      toast.error('Kunde inte spara anteckningar')
    } finally {
      setSavingNotes(false)
    }
  }, [adminNotes, adminNotesId, weekStart, user, data?.admin_notes])

  // Group assignments by day
  const assignmentsByDay = groupByDay(data?.assignments || [], currentWeekStart)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <ClipboardList className="h-7 w-7 text-nature-600 dark:text-nature-400" />
          <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100">
            Veckosammanfattning
          </h1>
        </div>
      </div>

      {/* Week navigation */}
      <div className="bg-white dark:bg-nature-800 rounded-lg shadow-sm border border-nature-200 dark:border-nature-700 p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentWeekStart(subWeeks(currentWeekStart, 1))}
            className="p-2 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-nature-600 dark:text-nature-400" />
          </button>

          <div className="text-center">
            <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
              Vecka {weekNumber}, {currentWeekStart.getFullYear()}
            </h2>
            <p className="text-sm text-nature-500 dark:text-nature-400">
              {weekStart} — {weekEnd}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {!isCurrentWeek && (
              <button
                onClick={() => setCurrentWeekStart(startOfWeek(new Date(), weekOptions))}
                className="text-sm px-3 py-1.5 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-md hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors"
              >
                Denna vecka
              </button>
            )}
            <button
              onClick={() => setCurrentWeekStart(addWeeks(currentWeekStart, 1))}
              className="p-2 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-nature-600 dark:text-nature-400" />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-nature-400" />
          <p className="ml-3 text-nature-600 dark:text-nature-400">Laddar veckosammanfattning...</p>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <SummaryCard
              label="Uppdrag"
              value={data?.total_assignments || 0}
              icon={<ClipboardList className="h-5 w-5 text-primary-500" />}
            />
            <SummaryCard
              label="Rapporterade timmar"
              value={data?.total_hours ? `${data.total_hours.toFixed(1).replace('.', ',')}` : '0'}
              icon={<Clock className="h-5 w-5 text-blue-500" />}
            />
            <SummaryCard
              label="Rapporterat"
              value={data?.reported_count || 0}
              icon={<CheckCircle2 className="h-5 w-5 text-green-500" />}
            />
            <SummaryCard
              label="Ej rapporterat"
              value={data?.unreported_count || 0}
              icon={<AlertCircle className="h-5 w-5 text-amber-500" />}
            />
          </div>

          {/* Assignments grouped by day */}
          <div className="space-y-4">
            {WEEKDAYS.map((dayName, dayIndex) => {
              const dayDate = format(addDays(currentWeekStart, dayIndex), 'yyyy-MM-dd')
              const dayAssignments = assignmentsByDay[dayIndex] || []

              return (
                <div key={dayIndex} className="bg-white dark:bg-nature-800 rounded-lg shadow-sm border border-nature-200 dark:border-nature-700">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-700 rounded-t-lg">
                    <h3 className="font-semibold text-nature-900 dark:text-nature-100">
                      {dayName} <span className="font-normal text-nature-500 dark:text-nature-400">{dayDate}</span>
                    </h3>
                    <span className="text-sm text-nature-500 dark:text-nature-400">
                      {dayAssignments.length} uppdrag
                    </span>
                  </div>

                  {dayAssignments.length === 0 ? (
                    <div className="px-4 py-6 text-center text-nature-400 dark:text-nature-500 text-sm">
                      Inga uppdrag
                    </div>
                  ) : (
                    <div className="divide-y divide-nature-100 dark:divide-nature-700">
                      {dayAssignments.map((assignment) => (
                        <AssignmentRow
                          key={assignment.id}
                          assignment={assignment}
                          onNavigate={() => navigate(`/admin/assignments/${assignment.id}`)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Admin notes */}
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-sm border border-nature-200 dark:border-nature-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-nature-900 dark:text-nature-100 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-nature-500" />
                Veckoanteckningar
              </h3>
              {savingNotes && (
                <span className="text-xs text-nature-400 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Sparar...
                </span>
              )}
            </div>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              onBlur={handleSaveNotes}
              placeholder="Skriv anteckningar för denna vecka..."
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-nature-300 dark:border-nature-600 bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100 placeholder-nature-400 dark:placeholder-nature-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y"
            />
          </div>
        </>
      )}
    </div>
  )
}

// Helper: group assignments by weekday index (0=Mon, 6=Sun)
function groupByDay(
  assignments: WeeklySummaryAssignment[],
  weekStart: Date
): Record<number, WeeklySummaryAssignment[]> {
  const groups: Record<number, WeeklySummaryAssignment[]> = {}
  for (const assignment of assignments) {
    const assignmentDate = parseISO(assignment.start_date)
    const dayIndex = Math.round((assignmentDate.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24))
    if (dayIndex >= 0 && dayIndex <= 6) {
      if (!groups[dayIndex]) groups[dayIndex] = []
      groups[dayIndex].push(assignment)
    }
  }
  return groups
}

// Summary card component
function SummaryCard({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-nature-800 rounded-lg shadow-sm border border-nature-200 dark:border-nature-700 p-4">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-sm text-nature-500 dark:text-nature-400">{label}</span>
      </div>
      <p className="text-2xl font-bold text-nature-900 dark:text-nature-100">{value}</p>
    </div>
  )
}

// Assignment row with TG reports
function AssignmentRow({
  assignment,
  onNavigate,
}: {
  assignment: WeeklySummaryAssignment
  onNavigate: () => void
}) {
  return (
    <div className="px-4 py-3">
      {/* Assignment header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <button
            onClick={onNavigate}
            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline text-left"
          >
            {assignment.assignment_number ? `#${assignment.assignment_number} ` : ''}
            {assignment.title}
          </button>
          <p className="text-sm text-nature-500 dark:text-nature-400">
            {assignment.customer_name}
            {assignment.start_time && (
              <span className="ml-2">
                {assignment.start_time}
                {assignment.end_time && `–${assignment.end_time}`}
              </span>
            )}
          </p>
        </div>
        <StatusBadge type="assignment" status={assignment.status} size="sm" />
      </div>

      {/* TG reports */}
      {assignment.subcontractor_reports.length > 0 && (
        <div className="ml-4 space-y-2">
          {assignment.subcontractor_reports.map((report) => (
            <div
              key={report.subcontractor_id}
              className="border-l-2 pl-3 py-1"
              style={{ borderColor: report.subcontractor_color }}
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: report.subcontractor_color }}
                />
                <span className="text-sm font-medium text-nature-700 dark:text-nature-300">
                  {report.subcontractor_name}
                </span>
                {report.tg_status && (
                  <StatusBadge type="tg-work" status={report.tg_status} size="sm" />
                )}
                {report.total_hours > 0 && (
                  <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {report.total_hours.toFixed(1).replace('.', ',')} tim
                  </span>
                )}
              </div>

              {/* Reported articles */}
              {report.reported_articles.length > 0 && (
                <div className="text-xs text-nature-600 dark:text-nature-400 space-y-0.5 ml-5">
                  {report.reported_articles.filter(a => !a.is_expense).map((article, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span>{article.article_name}</span>
                      <span className="text-nature-400">
                        {article.actual_quantity} {article.unit}
                      </span>
                      {article.notes && (
                        <span className="italic text-nature-400">— {article.notes}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Report comment */}
              {report.report_text && (
                <div className="ml-5 mt-1 text-xs italic text-nature-500 dark:text-nature-400 bg-nature-50 dark:bg-nature-700 rounded px-2 py-1">
                  "{report.report_text}"
                </div>
              )}

              {/* Not reported indicator */}
              {!report.reported_at && (
                <div className="ml-5 text-xs text-amber-500 dark:text-amber-400 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Ej rapporterat
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
